/**
 * Reactive graph data.
 *
 * Two levels, because they change at very different rates: the FULL graph is
 * rebuilt only when the index or the tree actually changes, while filters and
 * groups re-derive from it synchronously. Display sliders touch neither: the
 * renderer reads those every frame.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { searchService } from "@/data/search/client";
import { getData, subscribeDataContext } from "@/data/store";
import { useGraph, type GraphScope } from "@/store/graph";
import { applyFilters, buildGraph } from "./build";
import { compileQuery } from "./query";
import { loadGraphInput } from "./source";
import { EMPTY_GRAPH, type GraphCorpus, type GraphData } from "./types";

/** On top of the index's own 400ms reconcile debounce. */
const REBUILD_DEBOUNCE_MS = 150;

interface FullGraphState {
  data: GraphData;
  corpus?: GraphCorpus;
  loading: boolean;
}

export function useFullGraph(needsContent: boolean): FullGraphState {
  const [state, setState] = useState<FullGraphState>({ data: EMPTY_GRAPH, loading: true });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRevision = useRef(-1);
  const lastNeedsContent = useRef(needsContent);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    let disposed = false;

    const rebuild = async () => {
      const revision = searchService.revision;
      const wantText = lastNeedsContent.current;
      const { input, corpus } = await loadGraphInput(wantText);
      if (disposed || !alive.current) return;
      lastRevision.current = revision;
      setState({ data: buildGraph(input), ...(corpus ? { corpus } : {}), loading: false });
    };

    const schedule = (force = false) => {
      if (!force && searchService.revision === lastRevision.current) return;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void rebuild(), REBUILD_DEBOUNCE_MS);
    };

    void rebuild();
    const offIndexed = searchService.onIndexed(() => schedule());
    // titles, tags, trash and archive move without a body edit
    const offWorkspace = getData().workspace.subscribe(() => schedule(true));
    const offCtx = subscribeDataContext(() => schedule(true));

    return () => {
      disposed = true;
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
      offIndexed();
      offWorkspace();
      offCtx();
    };
  }, []);

  // a query gaining or losing bare terms changes whether bodies are needed
  useEffect(() => {
    if (lastNeedsContent.current === needsContent) return;
    lastNeedsContent.current = needsContent;
    lastRevision.current = -1;
  }, [needsContent]);

  return state;
}

export interface GraphView {
  data: GraphData;
  full: GraphData;
  loading: boolean;
  warnings: string[];
}

/** The filtered, group-coloured graph for one scope. */
export function useGraphView(scope: GraphScope): GraphView {
  const settings = useGraph((s) => (scope === "global" ? s.global : s.local));
  const compiled = useMemo(() => compileQuery(settings.filters.search), [settings.filters.search]);
  const groupsNeedContent = useMemo(
    () => settings.groups.some((g) => compileQuery(g.query).needsContent),
    [settings.groups],
  );
  const { data: full, corpus, loading } = useFullGraph(compiled.needsContent || groupsNeedContent);

  const data = useMemo(
    () => (full.nodes.length ? applyFilters(full, settings.filters, settings.groups, corpus) : EMPTY_GRAPH),
    [full, settings.filters, settings.groups, corpus],
  );

  return { data, full, loading, warnings: compiled.warnings };
}
